import React, { useState, useMemo, useCallback, memo, useRef } from "react";
import { useOpenInNewTab } from '@/hooks/useOpenInNewTab';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileText, Calendar, RefreshCw, Search, FolderOpen, ChevronDown, ChevronRight, Loader2, Menu, Plus, Trash2, Pencil, Sparkles, Download, CheckCircle } from "lucide-react";
import { useOfflineReport, useNetworkStatus } from "@/hooks/useOfflineReport";
import { openOfflineReport } from "@/services/offlineReportService";
import { useToast } from "@/hooks/use-toast";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { CreateReportDialog } from '@/components/CreateReportDialog';
import { EditReportDialog } from '@/components/EditReportDialog';
import { Sheet, SheetTrigger } from "@/components/ui/sheet";
import { format } from "date-fns";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { CommentForm } from '@/components/CommentForm';
import { useIsMobile } from "@/hooks/useMediaQuery";
import {
  getReportFileUrl,
  getReportAiFileUrl,
  hasReportFile,
  hasReportAiFile,
} from "@/integrations/supabase/reportFileService";

// Interface cho ReportRow props
interface ReportRowProps {
  report: any;
  userInfo: { name: string; email: string };
  isExpanded: boolean;
  onToggleExpansion: (reportId: any) => void;
  onOpenFile: (url: string) => void;
  onSummarizeReport: (reportId: any) => void;
  onDeleteReport?: (reportId: string) => void;
  onEditReport?: (report: any) => void;
  currentUserId?: string | null;
  formatDate: (date: any) => string;
  isSummarizing?: boolean;
}

// Memoized Report Row Component để tránh re-render không cần thiết
const ReportRow = memo<ReportRowProps>(({ report, userInfo, isExpanded, onToggleExpansion, onOpenFile, onSummarizeReport, onDeleteReport, onEditReport, currentUserId, formatDate, isSummarizing }) => {
  const canDelete = !!currentUserId && !!report.auth_user_id && report.auth_user_id === currentUserId;
  const canEdit = canDelete;
  const reportId = report.id || report.legacy_id || '';
  const { isOffline: isSavedOffline, isSaving, isChecking, saveProgress, saveOffline, checkFreshness } = useOfflineReport(reportId);
  const { isOnline } = useNetworkStatus();
  const { toast } = useToast();
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);

  const savePayload = () => ({
    id: reportId,
    report_name: report.report_name,
    period: report.period,
    created_on: report.created_on,
    full_url: report.full_url,
    file_url: report.file_url,
    file_id: report.file_id,
    supabase_file_path: report.supabase_file_path,
    summary_by_ai: report.summary_by_ai,
    is_summary: report.is_summary,
    id_group: report.id_group,
    created_by_user_id: report.created_by_user_id,
    auth_user_id: report.auth_user_id,
    updated_at: report.updated_at,
  });

  const handleSaveOffline = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    const result = await saveOffline(savePayload());
    if (result.success) {
      toast({ title: 'Đã lưu offline', description: `"${report.report_name}" sẵn sàng đọc khi mất mạng.` });
    } else {
      toast({ title: 'Không thể lưu offline', description: result.message, variant: 'destructive' });
    }
  }, [reportId, report, saveOffline, toast]);

  const handleCheckForUpdate = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isOnline) {
      toast({ title: 'Không có mạng', description: 'Không thể kiểm tra bản mới khi offline. Bản đã lưu vẫn nguyên.' });
      return;
    }
    const result = await checkFreshness();
    if (result.status === 'uptodate') {
      toast({ title: 'Đã là bản mới nhất', description: `"${report.report_name}" không có phiên bản mới.` });
    } else if (result.status === 'stale') {
      setUpdateDialogOpen(true);
    } else if (result.status === 'error') {
      toast({ title: 'Không thể kiểm tra', description: `${result.message} Bản đã lưu vẫn nguyên.`, variant: 'destructive' });
    }
  }, [isOnline, checkFreshness, report.report_name, toast]);

  const handleRedownloadOffline = useCallback(async () => {
    // Re-download without deleting first — saveOffline overwrites. If fetch
    // fails, the existing cached copy is preserved so an accidental click
    // can't destroy it.
    const result = await saveOffline(savePayload());
    if (result.success) {
      toast({ title: 'Đã tải lại', description: `"${report.report_name}" đã được cập nhật bản mới nhất.` });
    } else {
      toast({ title: 'Không thể tải lại', description: `${result.message} Bản cũ vẫn còn nguyên.`, variant: 'destructive' });
    }
  }, [reportId, report, saveOffline, toast]);

  const { opening: openingFile, open: openFileTab } = useOpenInNewTab();
  const { opening: openingAi, open: openAiTab } = useOpenInNewTab();

  const handleOpenFile = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    const fail = (title: string, description: string) =>
      toast({ title, description, variant: 'destructive' });

    if (isOnline) {
      // File tải bằng phiên đăng nhập (Storage hoặc edge function Drive) rồi
      // mới mở — hook lo tab chờ + spinner trên nút.
      await openFileTab(() => getReportFileUrl(report), (m) => fail('Không mở được file', m));
      return;
    }
    if (isSavedOffline) {
      await openFileTab(async () => {
        const blobUrl = await openOfflineReport(reportId);
        if (!blobUrl) fail('Không tìm thấy file offline', 'Vui lòng kết nối mạng để mở file này.');
        return blobUrl;
      }, (m) => fail('Không mở được file', m));
    } else {
      fail('Không có mạng', 'Báo cáo này chưa được lưu offline. Bấm "Lưu offline" khi có mạng để đọc sau.');
    }
  }, [isOnline, isSavedOffline, reportId, report, openFileTab, toast]);

  const handleOpenAiFile = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    // Bản AI không được lưu offline nên chỉ mở được khi có mạng.
    await openAiTab(
      () => getReportAiFileUrl(report),
      (m) => toast({ title: 'Không mở được bản AI', description: m, variant: 'destructive' })
    );
  }, [report, openAiTab, toast]);

  return (
    <>
      <TableRow 
        className="bg-white hover:bg-gray-50 cursor-pointer"
        onClick={() => onToggleExpansion(report.legacy_id || report.id)}
      >
        <TableCell className="pl-8 w-[35%]">
          <div className="flex items-center gap-2">
            <div className="flex-shrink-0 p-1">
              {isExpanded ? 
                <ChevronDown className="h-4 w-4" /> : 
                <ChevronRight className="h-4 w-4" />
              }
            </div>
            <span className="font-medium truncate">{report.report_name}</span>
          </div>
        </TableCell>
        <TableCell className="w-[25%]">
          <div className="flex flex-col">
            <span className="text-sm font-medium truncate">{userInfo.name}</span>
            <span className="text-xs text-gray-500 truncate">{userInfo.email}</span>
          </div>
        </TableCell>
        <TableCell className="w-[20%]">
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <Calendar className="h-3 w-3" />
            <span>{report.created_on ? formatDate(report.created_on) : 'N/A'}</span>
          </div>
        </TableCell>
        <TableCell className="w-[20%]">
          <div className="flex items-center gap-1 flex-wrap">
            {/* Nút Lưu / Đã lưu offline */}
            {isSavedOffline ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCheckForUpdate}
                  disabled={isChecking || isSaving}
                  className="border-green-500 text-green-700 hover:bg-green-50"
                  title="Đã lưu offline — bấm để kiểm tra bản mới"
                >
                  {isChecking || isSaving ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <CheckCircle className="h-3 w-3" />
                  )}
                </Button>
                <AlertDialog open={updateDialogOpen} onOpenChange={setUpdateDialogOpen}>
                  <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Có bản mới — tải lại?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Báo cáo "<strong>{report.report_name}</strong>" trên máy chủ đã được cập nhật sau lần lưu offline. Bạn có muốn tải bản mới không? Nếu hủy, bản cũ vẫn được giữ nguyên.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Hủy</AlertDialogCancel>
                      <AlertDialogAction onClick={handleRedownloadOffline}>
                        Tải lại
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={handleSaveOffline}
                disabled={isSaving}
                title="Lưu để đọc khi không có mạng"
                className="text-gray-500 hover:text-gray-700"
              >
                {isSaving ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Download className="h-3 w-3" />
                )}
              </Button>
            )}
            {hasReportFile(report) && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenFile}
                disabled={openingFile}
              >
                {openingFile ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <FolderOpen className="h-3 w-3 mr-1" />}
                {openingFile ? 'Đang mở…' : 'Mở'}
              </Button>
            )}
            {hasReportAiFile(report) && (
              <Button
                variant="outline"
                size="sm"
                className="border-purple-500 text-purple-600 hover:bg-purple-500 hover:text-white"
                onClick={handleOpenAiFile}
                disabled={openingAi}
              >
                {openingAi ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
                {openingAi ? 'Đang mở…' : 'Mở AI'}
              </Button>
            )}
            {canEdit && onEditReport && (
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-foreground"
                onClick={(e) => {
                  e.stopPropagation();
                  onEditReport(report);
                }}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            )}
            {canDelete && onDeleteReport && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Xóa báo cáo?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Báo cáo "<strong>{report.report_name}</strong>" sẽ bị xóa vĩnh viễn. Hành động này không thể hoàn tác.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Hủy</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      onClick={() => onDeleteReport(report.id)}
                    >
                      Xóa
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </TableCell>
      </TableRow>
      {/* Expanded Description Row */}
      {isExpanded && (
        <TableRow className="bg-blue-50">
          <TableCell colSpan={4} className="pl-16">
            <div className="py-4">
              <p className="text-sm text-gray-700 font-medium mb-2">Tóm tắt bằng AI:</p>
              <div className="text-sm text-gray-600 prose prose-sm max-w-none">
                {report.summary_by_ai ? (
                  <div className="markdown-content">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw]}
                      components={{
                        h1: ({children}) => <h1 className="text-lg font-bold mb-2 text-gray-800">{children}</h1>,
                        h2: ({children}) => <h2 className="text-base font-semibold mb-2 text-gray-800">{children}</h2>,
                        h3: ({children}) => <h3 className="text-sm font-semibold mb-1 text-gray-700">{children}</h3>,
                        p: ({children}) => <p className="mb-2 text-gray-600 leading-relaxed">{children}</p>,
                        ul: ({children}) => <ul className="list-disc pl-4 mb-2 space-y-1">{children}</ul>,
                        ol: ({children}) => <ol className="list-decimal pl-4 mb-2 space-y-1">{children}</ol>,
                        li: ({children}) => <li className="text-gray-600">{children}</li>,
                        strong: ({children}) => <strong className="font-semibold text-gray-700">{children}</strong>,
                        em: ({children}) => <em className="italic text-gray-600">{children}</em>,
                        code: ({children}) => <code className="bg-gray-100 px-1 py-0.5 rounded text-xs font-mono text-gray-800">{children}</code>,
                        blockquote: ({children}) => <blockquote className="border-l-4 border-blue-200 pl-3 py-1 bg-blue-25 text-gray-600 italic">{children}</blockquote>,
                        br: () => <br className="my-1" />,
                        table: ({children}) => (
                          <div className="overflow-x-auto my-4">
                            <table className="min-w-full border-collapse border border-gray-300">
                              {children}
                            </table>
                          </div>
                        ),
                        thead: ({children}) => <thead className="bg-gray-50">{children}</thead>,
                        tbody: ({children}) => <tbody className="bg-white">{children}</tbody>,
                        tr: ({children}) => <tr className="border-b border-gray-200">{children}</tr>,
                        th: ({children}) => (
                          <th className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            {children}
                          </th>
                        ),
                        td: ({children}) => (
                          <td className="border border-gray-300 px-3 py-2 text-sm text-gray-600 align-top">
                            {children}
                          </td>
                        )
                      }}
                    >
                      {report.summary_by_ai}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <p className="text-gray-500 italic">Chưa có tóm tắt AI</p>
                )}
              </div>
              {/* Comment form */}
              <CommentForm 
                reportId={report.legacy_id || report.id} 
                reportName={report.report_name}
                reportOwnerName={userInfo.name}
                reportOwnerEmail={userInfo.email}
              />
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
});

ReportRow.displayName = 'ReportRow';

interface ReportListViewProps {
  activeTab: string;
  searchTerm: string;
  expandedRows: Set<any>;
  expandedPeriods: Set<string>;
  refreshingReports: Set<any>;
  paginatedPeriods: any;
  paginatedGroupPeriods: any;
  groupedReports: any;
  onSearchChange: (term: string) => void;
  onToggleRowExpansion: (reportId: any) => void;
  onTogglePeriodExpansion: (period: string) => void;
  onOpenFile: (url: string) => void;
  onSummarizeReport: (reportId: any) => void;
  onDrawerToggle: () => void;
  onDeleteReport?: (reportId: string) => void;
  onEditReport?: (report: any) => void;
  currentUserId?: string | null;
  getUserInfo: (userId?: string | null, authUserId?: string | null) => { name: string; email: string };
  formatDate: (date: any) => string;
}

// Helper function for getting group icon and color (copied from ReportGroupsSidebar for consistency)
const getGroupIcon = (groupName: string) => {
  const name = groupName?.toLowerCase() || '';
  
  if (name.includes('tuần') || name.includes('weekly')) return import('lucide-react').then(mod => mod.TrendingUp);
  if (name.includes('tháng') || name.includes('month')) return import('lucide-react').then(mod => mod.BarChart3);
  if (name.includes('quý') || name.includes('quarter')) return import('lucide-react').then(mod => mod.PieChart);
  if (name.includes('năm') || name.includes('year') || name.includes('annual')) return import('lucide-react').then(mod => mod.Activity);
  if (name.includes('tài chính') || name.includes('finance') || name.includes('financial')) return import('lucide-react').then(mod => mod.DollarSign);
  if (name.includes('nhân sự') || name.includes('hr') || name.includes('human')) return import('lucide-react').then(mod => mod.Users);
  if (name.includes('kế hoạch') || name.includes('plan') || name.includes('target')) return import('lucide-react').then(mod => mod.Target);
  if (name.includes('báo cáo') || name.includes('report')) return FileText;
  
  return import('lucide-react').then(mod => mod.Building2);
};

const getGroupColor = (groupName: string) => {
  const name = groupName?.toLowerCase() || '';
  if (name.includes('tuần') || name.includes('weekly')) return { icon: 'text-green-600', badge: 'bg-green-100 text-green-800' };
  if (name.includes('tháng') || name.includes('month')) return { icon: 'text-purple-600', badge: 'bg-purple-100 text-purple-800' };
  if (name.includes('quý') || name.includes('quarter')) return { icon: 'text-orange-600', badge: 'bg-orange-100 text-orange-800' };
  if (name.includes('năm') || name.includes('year')) return { icon: 'text-red-600', badge: 'bg-red-100 text-red-800' };
  if (name.includes('tài chính') || name.includes('finance')) return { icon: 'text-emerald-600', badge: 'bg-emerald-100 text-emerald-800' };
  if (name.includes('nhân sự') || name.includes('hr')) return { icon: 'text-indigo-600', badge: 'bg-indigo-100 text-indigo-800' };
  return { icon: 'text-gray-600', badge: 'bg-gray-100 text-gray-800' };
};

const ReportListView = memo(({
  activeTab,
  searchTerm,
  expandedRows,
  expandedPeriods,
  refreshingReports,
  paginatedPeriods,
  paginatedGroupPeriods,
  groupedReports,
  onSearchChange,
  onToggleRowExpansion,
  onTogglePeriodExpansion,
  onOpenFile,
  onSummarizeReport,
  onDrawerToggle,
  onDeleteReport,
  onEditReport,
  currentUserId,
  getUserInfo,
  formatDate
}: ReportListViewProps) => {
  // 🔍 DEBUGGING: Track ReportListView re-renders
  const renderCount = useRef(0);
  const lastRenderTime = useRef(Date.now());
  const renderStartTime = Date.now();
  renderCount.current++;
  
  console.log(`📊 ReportListView render #${renderCount.current}`, {
    activeTab,
    expandedPeriodsCount: expandedPeriods.size,
    timeSinceLastRender: renderStartTime - lastRenderTime.current,
    timestamp: new Date().toISOString().slice(11, 23)
  });
  
  // DEBUG: Log paginatedGroupPeriods for "BÁO CÁO THÁNG"
  if (activeTab === '131CBB23-FB62A828') {
    console.log('🔍 ReportListView DEBUG "BÁO CÁO THÁNG":', {
      activeTab,
      totalPeriods: paginatedGroupPeriods.totalPeriods,
      periodsInData: Object.keys(paginatedGroupPeriods.data || {}),
      reportsPerPeriod: Object.entries(paginatedGroupPeriods.data || {}).map(([period, reports]) => ({
        period,
        count: Array.isArray(reports) ? reports.length : 0
      }))
    });
  }
  
  lastRenderTime.current = renderStartTime;

  const isMobile = useIsMobile();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingReport, setEditingReport] = useState<any | null>(null);

  const handleEditReport = useCallback((report: any) => {
    setEditingReport(report);
    if (onEditReport) onEditReport(report);
  }, [onEditReport]);

  const renderAllReports = () => (
    <Card>
      <CardHeader>
        {isMobile ? (
          // Mobile Layout: Title on top, controls below
          <>
            {/* Row 1: Title only - full width */}
            <CardTitle className="flex items-center gap-2 mb-4">
              <FileText className="h-5 w-5 text-blue-600" />
              Tất cả báo cáo ({paginatedPeriods.totalPeriods} periods)
            </CardTitle>
            
            {/* Row 2: Menu button + Search + Create */}
            <div className="flex items-center gap-2">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" size="sm" className="flex-shrink-0" onClick={onDrawerToggle}>
                    <Menu className="h-4 w-4" />
                  </Button>
                </SheetTrigger>
              </Sheet>
              
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Tìm kiếm theo tên, period..."
                  value={searchTerm}
                  onChange={(e) => onSearchChange(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button size="sm" className="flex-shrink-0" onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </>
        ) : (
          // Desktop Layout: Original single row
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={onDrawerToggle}>
                <Menu className="h-4 w-4" />
              </Button>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                Tất cả báo cáo ({paginatedPeriods.totalPeriods} periods)
              </CardTitle>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Tìm kiếm theo tên, period, người tạo..."
                  value={searchTerm}
                  onChange={(e) => onSearchChange(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button size="sm" onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Tạo báo cáo
              </Button>
            </div>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {paginatedPeriods.totalPeriods > 0 ? (
          <div className="w-full overflow-x-auto">
            <Table className="table-fixed w-full min-w-[800px]">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[35%]">Tên báo cáo / Period</TableHead>
                  <TableHead className="w-[25%]">Người tạo</TableHead>
                  <TableHead className="w-[20%]">Ngày tạo</TableHead>
                  <TableHead className="w-[20%]">Thao tác</TableHead>
                </TableRow>
              </TableHeader>
            <TableBody>
              {Object.entries(paginatedPeriods.data).map(([period, reports]) => {
                const isExpanded = expandedPeriods.has(period);
                return (
                  <React.Fragment key={`period-${period}`}>
                    {/* Period Header Row - Clickable */}
                    <TableRow 
                      className="bg-gray-50 font-semibold cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => onTogglePeriodExpansion(period)}
                    >
                      <TableCell className="font-bold text-gray-700 w-[35%]">
                        <div className="flex items-center gap-2">
                          {isExpanded ? (
                            <ChevronDown className="h-5 w-5 flex-shrink-0" />
                          ) : (
                            <ChevronRight className="h-5 w-5 flex-shrink-0" />
                          )}
                          <span className="whitespace-nowrap">{period} ({(reports as any[]).length} báo cáo)</span>
                        </div>
                      </TableCell>
                      <TableCell className="w-[25%]"></TableCell>
                      <TableCell className="w-[20%]"></TableCell>
                      <TableCell className="w-[20%]"></TableCell>
                    </TableRow>
                    {/* Reports under this period - Only show when expanded */}
                    {isExpanded && (reports as any[]).map((report: any) => {
                      const userInfo = getUserInfo(report.created_by_user_id, report.auth_user_id);
                      const isReportExpanded = expandedRows.has(report.legacy_id || report.id);
                      const isSummarizing = refreshingReports.has(report.legacy_id || report.id);
                      return (
                        <ReportRow
                          key={report.legacy_id || report.id}
                          report={report}
                          userInfo={userInfo}
                          isExpanded={isReportExpanded}
                          onToggleExpansion={onToggleRowExpansion}
                          onOpenFile={onOpenFile}
                          onSummarizeReport={onSummarizeReport}
                          onDeleteReport={onDeleteReport}
                          onEditReport={handleEditReport}
                          currentUserId={currentUserId}
                          formatDate={formatDate}
                          isSummarizing={isSummarizing}
                        />
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Không tìm thấy báo cáo nào</p>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderGroupReports = () => {
    const selectedGroup = Object.values(groupedReports).find((group: any) => 
      (group.ID || group.GroupID || group.id || '').toString() === activeTab
    ) as any;
    
    if (!selectedGroup) return null;
    
    const IconComponent = FileText; // Simplified for now
    const colors = getGroupColor(selectedGroup.GroupName || selectedGroup.GroupNameShort || '');
    
    return (
      <Card>
        <CardHeader>
          {isMobile ? (
            // Mobile Layout: Title on top, controls below
            <>
              {/* Row 1: Title only - full width */}
              <CardTitle className="flex items-center gap-2 mb-4">
                <IconComponent className={`h-5 w-5 ${colors.icon}`} />
                {selectedGroup.GroupName || selectedGroup.GroupNameShort || 'Nhóm không tên'} ({paginatedGroupPeriods.totalPeriods} periods)
              </CardTitle>
              
              {/* Row 2: Menu button + Search + Create */}
              <div className="flex items-center gap-2">
                <Sheet>
                  <SheetTrigger asChild>
                    <Button variant="outline" size="sm" className="flex-shrink-0" onClick={onDrawerToggle}>
                      <Menu className="h-4 w-4" />
                    </Button>
                  </SheetTrigger>
                </Sheet>
                
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    placeholder="Tìm kiếm theo tên, period..."
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Button size="sm" className="flex-shrink-0" onClick={() => setCreateDialogOpen(true)}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Description below if exists */}
              {selectedGroup.Descrt && (
                <p className="text-muted-foreground mt-3">{selectedGroup.Descrt}</p>
              )}
            </>
          ) : (
            // Desktop Layout: Original single row
            <>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Button variant="outline" size="sm" onClick={onDrawerToggle}>
                    <Menu className="h-4 w-4" />
                  </Button>
                  <CardTitle className="flex items-center gap-2">
                    <IconComponent className={`h-5 w-5 ${colors.icon}`} />
                    {selectedGroup.GroupName || selectedGroup.GroupNameShort || 'Nhóm không tên'} ({paginatedGroupPeriods.totalPeriods} periods)
                  </CardTitle>
                </div>
                <div className="flex items-center gap-3">
                  <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <Input
                      placeholder="Tìm kiếm theo tên, period, người tạo..."
                      value={searchTerm}
                      onChange={(e) => onSearchChange(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Button size="sm" onClick={() => setCreateDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-1" />
                    Tạo báo cáo
                  </Button>
                </div>
              </div>
              {selectedGroup.Descrt && (
                <p className="text-muted-foreground">{selectedGroup.Descrt}</p>
              )}
            </>
          )}
        </CardHeader>
        <CardContent>
          {paginatedGroupPeriods.totalPeriods > 0 ? (
            <div className="w-full overflow-x-auto">
              <Table className="table-fixed w-full min-w-[800px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[35%]">Tên báo cáo / Period</TableHead>
                    <TableHead className="w-[25%]">Người tạo</TableHead>
                    <TableHead className="w-[20%]">Ngày tạo</TableHead>
                    <TableHead className="w-[20%]">Thao tác</TableHead>
                  </TableRow>
                </TableHeader>
              <TableBody>
                {Object.entries(paginatedGroupPeriods.data).map(([period, reports]) => {
                  const isExpanded = expandedPeriods.has(period);
                  return (
                    <React.Fragment key={`group-period-${period}`}>
                      {/* Period Header Row - Clickable */}
                      <TableRow 
                        className="bg-gray-50 font-semibold cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => onTogglePeriodExpansion(period)}
                      >
                        <TableCell className="font-bold text-gray-700 w-[35%]">
                          <div className="flex items-center gap-2">
                            {isExpanded ? (
                              <ChevronDown className="h-5 w-5 flex-shrink-0" />
                            ) : (
                              <ChevronRight className="h-5 w-5 flex-shrink-0" />
                            )}
                            <span className="whitespace-nowrap">{period} ({(reports as any[]).length} báo cáo)</span>
                          </div>
                        </TableCell>
                        <TableCell className="w-[25%]"></TableCell>
                        <TableCell className="w-[20%]"></TableCell>
                        <TableCell className="w-[20%]"></TableCell>
                      </TableRow>
                      {/* Reports under this period - Only show when expanded */}
                      {isExpanded && (reports as any[]).map((report: any) => {
                        const userInfo = getUserInfo(report.created_by_user_id, report.auth_user_id);
                        const isReportExpanded = expandedRows.has(report.legacy_id || report.id);
                        const isSummarizing = refreshingReports.has(report.legacy_id || report.id);
                        return (
                        <ReportRow
                          key={report.legacy_id || report.id}
                          report={report}
                          userInfo={userInfo}
                          isExpanded={isReportExpanded}
                          onToggleExpansion={onToggleRowExpansion}
                          onOpenFile={onOpenFile}
                          onSummarizeReport={onSummarizeReport}
                          onDeleteReport={onDeleteReport}
                          onEditReport={handleEditReport}
                          currentUserId={currentUserId}
                          formatDate={formatDate}
                          isSummarizing={isSummarizing}
                        />
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <IconComponent className={`h-12 w-12 mx-auto mb-4 opacity-50 ${colors.icon}`} />
            <p>Nhóm này chưa có báo cáo nào</p>
          </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 overflow-y-auto p-6">
        {activeTab === "all" ? renderAllReports() : renderGroupReports()}
      </div>

      <CreateReportDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        defaultGroupId={activeTab !== 'all' ? activeTab : undefined}
      />

      <EditReportDialog
        open={!!editingReport}
        onOpenChange={(open) => { if (!open) setEditingReport(null); }}
        report={editingReport}
      />
    </div>
  );
});

export { ReportListView };